/**
 * API client for the "Not on the Website" page (/audit/not-on-website/*),
 * revenue-assurance plan, Phase 3. Mirrors backend/schemas.py's NotOnWebsite*
 * models. Every figure is a fact from the latest audit run: the dollar total
 * is the HomeNet Selling Price of the dark cars added up, never an estimate.
 */
import { API } from "../config/api"

export interface NotOnWebsiteVehicle {
  vin: string
  stock: string
  vehicle: string
  rooftop: string
  problem_type: string
  label: string
  /** Whole days INVI has seen the car invisible; null when first-seen was never recorded. */
  days_dark: number | null
  photos_count: number | null
  /** HomeNet Selling Price; null when the export carried none for the car. */
  selling_price: number | null
  website_url: string | null
}

export interface NotOnWebsiteRooftop {
  rooftop: string
  cars: number
  no_price_cars: number
  selling_price_total: number
}

/** One saved audit run on the line. */
export interface NotOnWebsitePoint {
  taken_at: string
  cars: number
  selling_price_total: number
  /** Cars counted but left out of the total for lack of a price. Optional only
   * so older test fixtures stay valid; the backend always sends it. */
  no_price_cars?: number | null
  /** Null on a store's points saved before the per-store sold breakdown
   * existed, so a store's sold line starts where the data does. */
  sold_still_advertised: number | null
  sold_no_price_cars?: number | null
  /** The website's advertised price of those listings; null on points saved
   * before it was recorded. */
  sold_selling_price_total: number | null
  days_dark_avg: number | null
}

/** One counted problem behind the headline tile: its cars and their dollars. */
export interface NotOnWebsiteProblem {
  problem_type: string
  label: string
  cars: number
  no_price_cars: number
  selling_price_total: number
}

/** The same problem still inside its grace window — not counted yet. */
export interface NotOnWebsiteWindowed {
  problem_type: string
  label: string
  cars: number
}

/** The newest saved point, for the tile on the audit page and the home-page
 * widget. All figures null until the first chained run saves one; `previous`
 * is the point before it, for "since the previous run". */
export interface NotOnWebsiteSummary {
  /** The dealer's website, by name ("dolanautogroup.com"); null when unconfigured. */
  website_domain: string | null
  /** The store the figures are for (the audit label, "Dodge"); null = the whole group. */
  rooftop_label: string | null
  taken_at: string | null
  cars: number | null
  no_price_cars: number | null
  selling_price_total: number | null
  sold_still_advertised: number | null
  sold_no_price_cars: number | null
  sold_selling_price_total: number | null
  previous: NotOnWebsitePoint | null
}

export interface NotOnWebsiteResponse {
  run_id: number
  run_completed_at: string | null
  /** The dealer's website, by name ("dolanautogroup.com"); null when unconfigured. */
  website_domain: string | null
  /** The store the page is showing (the audit label, "Dodge"); null = the whole group. */
  rooftop_label: string | null
  cars: number
  priced_cars: number
  no_price_cars: number
  selling_price_total: number
  days_dark_avg: number | null
  days_dark_oldest: number | null
  days_dark_youngest: number | null
  sold_still_advertised: number
  /** Those listings' website-advertised prices: how many carry one, how many
   * do not, and the total over the ones that do. */
  sold_priced_cars: number
  sold_no_price_cars: number
  sold_selling_price_total: number
  /** Sold listings still inside their window — not counted in the tile. */
  sold_inside_window: number
  /** The breakdown behind the headline tile, and what is still inside its window. */
  by_problem: NotOnWebsiteProblem[]
  inside_window: NotOnWebsiteWindowed[]
  /** When the group's first morning alert went out; the mark on the line. */
  alerts_started_at: string | null
  /** Latest total minus the first point after that mark; null until one exists. */
  change_since_alerts: number | null
  by_rooftop: NotOnWebsiteRooftop[]
  vehicles: NotOnWebsiteVehicle[]
  /** The sold-still-advertised listings, longest up first, each with its link. */
  sold_vehicles: NotOnWebsiteVehicle[]
  history: NotOnWebsitePoint[]
}

const TOKEN_KEY = "invi_token"

function authHeaders(): HeadersInit {
  const token = localStorage.getItem(TOKEN_KEY)
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function errorDetail(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json()
    if (body.detail) return body.detail
  } catch {
    // fall through with the generic message
  }
  return fallback
}

/**
 * The view's query string: the group, and the store when one is selected.
 * The store is the audit page's rooftop slug; the backend resolves it with
 * the same resolver the audit page uses.
 */
function viewParams(dealerGroupId: number, rooftopSlug?: string | null): string {
  const params = new URLSearchParams({ dealer_group_id: String(dealerGroupId) })
  if (rooftopSlug) params.set("rooftop_slug", rooftopSlug)
  return `?${params.toString()}`
}

/**
 * The page. Resolves to null when the group has no audit run yet — the page
 * shows that as an empty state, not as a failure.
 */
export async function getNotOnWebsite(
  dealerGroupId: number,
  rooftopSlug?: string | null,
): Promise<NotOnWebsiteResponse | null> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.notOnWebsite.base}${viewParams(dealerGroupId, rooftopSlug)}`,
    { headers: authHeaders() }
  )
  if (res.status === 404) return null
  if (!res.ok) throw new Error(await errorDetail(res, `Request failed: ${res.status}`))
  return res.json()
}

export async function getNotOnWebsiteSummary(
  dealerGroupId: number,
  rooftopSlug?: string | null,
): Promise<NotOnWebsiteSummary> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.notOnWebsite.summary}${viewParams(dealerGroupId, rooftopSlug)}`,
    { headers: authHeaders() }
  )
  if (!res.ok) throw new Error(await errorDetail(res, `Request failed: ${res.status}`))
  return res.json()
}

export async function exportNotOnWebsiteWorkbook(
  dealerGroupId: number,
  rooftopSlug?: string | null,
): Promise<Blob> {
  const res = await fetch(
    `${API.baseUrl}${API.endpoints.notOnWebsite.xlsx}${viewParams(dealerGroupId, rooftopSlug)}`,
    { headers: authHeaders() }
  )
  if (!res.ok) throw new Error(await errorDetail(res, `Download failed: ${res.status}`))
  return res.blob()
}

/**
 * Dollars the way the page says them: "$2.18M" once the total passes a
 * million, "$978,400" below it. The table always shows the full figure.
 */
export function formatSellingPrice(value: number, compact = false): string {
  if (compact && Math.abs(value) >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(2)}M`
  }
  return `$${Math.round(value).toLocaleString()}`
}
